import React from "react";
import {
  UtensilsCrossed,
  Home,
  Dumbbell,
  Building2,
  Gift,
  Tent,
  GraduationCap,
  Hotel,
  Stethoscope,
  HardHat,
  Church,
  Car,
} from "lucide-react";

const USE_CASES = [
  {
    title: "Restaurants & Cafés",
    icon: UtensilsCrossed,
    image:
      "https://images.pexels.com/photos/17057034/pexels-photo-17057034.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    blurb:
      "Hand-deliver a branded bottle with every check. Elevate the moment guests leave — make them remember the name.",
    span: "lg:col-span-4 lg:row-span-2",
  },
  {
    title: "Gyms & Fitness",
    icon: Dumbbell,
    image:
      "https://images.pexels.com/photos/7031705/pexels-photo-7031705.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    blurb:
      "Members are walking ads. Hydrate them, brand them, keep your studio top of mind from class to commute.",
    span: "lg:col-span-4",
  },
  {
    title: "Real Estate",
    icon: Home,
    image:
      "https://images.pexels.com/photos/33397572/pexels-photo-33397572.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    blurb:
      "Closing gift, open-house staple, brokerage swag. The bottle stays in their new home long after the deal closes.",
    span: "lg:col-span-4",
  },
  {
    title: "Corporate Offices",
    icon: Building2,
    blurb:
      "Stocked in boardrooms, lobbies and reception. Welcome clients, reward teams, anchor your culture.",
    span: "lg:col-span-3",
  },
  {
    title: "Events & Trade Shows",
    icon: Tent,
    blurb:
      "Be the booth everyone remembers. Branded bottles + QR drops = activation that actually converts.",
    span: "lg:col-span-3",
  },
  {
    title: "Schools & Colleges",
    icon: GraduationCap,
    blurb:
      "Orientation kits, sports day, alumni drives. Promote school spirit while keeping everyone refreshed.",
    span: "lg:col-span-3",
  },
  {
    title: "Hotels & Hospitality",
    icon: Hotel,
    blurb:
      "In-room amenity, conference table staple, valet gift. A high-end touch that costs less than a chocolate.",
    span: "lg:col-span-3",
  },
  {
    title: "Healthcare & Clinics",
    icon: Stethoscope,
    blurb:
      "Patient appreciation, clinic launches, dental kits. Show you care with a refreshing, thoughtful gesture.",
    span: "lg:col-span-4",
  },
  {
    title: "Construction & Industries",
    icon: HardHat,
    blurb:
      "Keep crews hydrated, keep your brand front and center on every job site, every truck, every hand.",
    span: "lg:col-span-4",
  },
  {
    title: "Auto Dealerships",
    icon: Car,
    blurb:
      "Hand one to every test drive. Branded bottles ride home with the new car — and the new story.",
    span: "lg:col-span-4",
  },
];

export const UseCases = () => {
  return (
    <section
      id="use-cases"
      data-testid="usecases-section"
      className="py-24 sm:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-10 items-end mb-16">
          <div>
            <div className="overline mb-4">We Work Across Every Segment</div>
            <h2 className="font-display text-4xl sm:text-6xl font-black tracking-tighter leading-[0.92]">
              From the corner café
              <br />
              <span className="font-editorial italic font-medium">
                to the corner office.
              </span>
            </h2>
          </div>
          <p className="text-slate-600 text-lg md:text-right">
            One product. Endless campaigns. We design custom labels for any
            industry — and ship across Canada from Mississauga.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-5 auto-rows-fr">
          {USE_CASES.map((u, i) => {
            const Icon = u.icon;
            const hasImage = !!u.image;
            return (
              <div
                key={u.title}
                data-testid={`usecase-${i}`}
                className={`relative rounded-2xl overflow-hidden border border-slate-200 card-lift ${
                  u.span || "lg:col-span-4"
                } ${hasImage ? "min-h-[320px] bg-slate-900" : "min-h-[260px] bg-white"}`}
              >
                {hasImage && (
                  <>
                    <img
                      src={u.image}
                      alt={u.title}
                      className="absolute inset-0 w-full h-full object-cover opacity-80"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent" />
                  </>
                )}
                <div
                  className={`relative h-full p-7 flex flex-col justify-between ${
                    hasImage ? "text-white" : "text-slate-900"
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div
                      className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
                        hasImage
                          ? "bg-white/15 backdrop-blur-md text-white"
                          : "bg-[var(--tq-navy)] text-white"
                      }`}
                    >
                      <Icon size={20} />
                    </div>
                    <span
                      className={`font-mono text-xs ${
                        hasImage ? "text-white/60" : "text-slate-400"
                      }`}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-display text-2xl font-bold tracking-tight mb-2">
                      {u.title}
                    </h3>
                    <p
                      className={`text-sm leading-relaxed ${
                        hasImage ? "text-white/85" : "text-slate-600"
                      }`}
                    >
                      {u.blurb}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Sweep banner */}
        <div className="mt-10 rounded-2xl bg-[var(--tq-navy)] text-white px-6 sm:px-10 py-6 flex flex-wrap items-center justify-between gap-4">
          <div className="font-display font-black text-lg sm:text-2xl tracking-tight">
            And many more — Churches, Non-profits, Banks, Retail, Automotive.
          </div>
          <a
            href="#contact"
            data-testid="usecases-cta"
            className="tq-btn tq-btn-light"
          >
            Pitch your use case
          </a>
        </div>
      </div>
    </section>
  );
};
